import { z } from "zod"

import { commandEnvelopeV1Schema } from "./command-envelope-v1.js"
import {
  entityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

export const FIELD_MEDIA_MAXIMUM_BYTES = 20 * 1024 * 1024
export const FIELD_MEDIA_CHUNK_BYTES = 256 * 1024

export const fieldMediaTypeV1Schema = z.enum([
  "image/jpeg",
  "image/png",
  "application/pdf",
])

export const fieldMediaUploadInitiateV1Schema = z
  .object({
    requestVersion: z.literal(1),
    mediaId: entityIdSchema,
    visitId: entityIdSchema,
    fileName: z
      .string()
      .min(1)
      .max(128)
      .regex(/^[A-Za-z0-9][A-Za-z0-9._ -]*$/),
    mediaType: fieldMediaTypeV1Schema,
    sizeBytes: z.number().int().min(1).max(FIELD_MEDIA_MAXIMUM_BYTES).safe(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    capturedAt: z.iso.datetime({ offset: true }),
    associationCommand: commandEnvelopeV1Schema,
  })
  .strict()
  .superRefine((request, context) => {
    const command = request.associationCommand
    const payload = command.payload
    if (
      command.commandType !== "visit.media.associate" ||
      command.commandVersion !== 1 ||
      command.context.entityType !== "visit" ||
      command.context.entityId !== request.visitId ||
      command.expectedVersion === null ||
      payload.mediaId !== request.mediaId ||
      payload.visitId !== request.visitId ||
      payload.fileName !== request.fileName ||
      payload.mediaType !== request.mediaType ||
      payload.sizeBytes !== request.sizeBytes ||
      payload.sha256 !== request.sha256 ||
      payload.capturedAt !== request.capturedAt ||
      payload.storageReference !== `field-media:${request.mediaId}`
    ) {
      context.addIssue({
        code: "custom",
        path: ["associationCommand"],
        message: "Media association command must match the upload.",
      })
    }
  })

export const fieldMediaUploadSessionV1Schema = z
  .object({
    responseVersion: z.literal(1),
    uploadId: entityIdSchema,
    mediaId: entityIdSchema,
    resumeToken: z.string().min(32).max(512),
    nextOffset: z
      .number()
      .int()
      .min(0)
      .max(FIELD_MEDIA_MAXIMUM_BYTES)
      .safe(),
    chunkSizeBytes: z.literal(FIELD_MEDIA_CHUNK_BYTES),
    expiresAt: z.iso.datetime({ offset: true }),
  })
  .strict()

export const fieldMediaUploadFinalizeV1Schema = z
  .object({
    requestVersion: z.literal(1),
    resumeToken: z.string().min(32).max(512),
  })
  .strict()

export const fieldMediaUploadResultV1Schema = z
  .object({
    responseVersion: z.literal(1),
    uploadId: entityIdSchema,
    mediaId: entityIdSchema,
    status: z.enum(["uploading", "scanned", "associated"]),
    nextOffset: z
      .number()
      .int()
      .min(0)
      .max(FIELD_MEDIA_MAXIMUM_BYTES)
      .safe(),
    storageReference: z
      .string()
      .regex(/^field-media:[0-9a-f-]{36}$/)
      .nullable(),
  })
  .strict()

export const fieldMediaQueueSummaryV1Schema = z
  .object({
    responseVersion: z.literal(1),
    tenantId: tenantIdSchema,
    membershipId: membershipIdSchema,
    pending: z.number().int().nonnegative().max(100),
    failed: z.number().int().nonnegative().max(100),
  })
  .strict()

export type FieldMediaTypeV1 = z.infer<typeof fieldMediaTypeV1Schema>
export type FieldMediaUploadInitiateV1 = z.infer<
  typeof fieldMediaUploadInitiateV1Schema
>
export type FieldMediaUploadSessionV1 = z.infer<
  typeof fieldMediaUploadSessionV1Schema
>
export type FieldMediaUploadFinalizeV1 = z.infer<
  typeof fieldMediaUploadFinalizeV1Schema
>
export type FieldMediaUploadResultV1 = z.infer<
  typeof fieldMediaUploadResultV1Schema
>
export type FieldMediaQueueSummaryV1 = z.infer<
  typeof fieldMediaQueueSummaryV1Schema
>
